import { useMemo, useState } from 'react'
import compact from 'lodash/compact'
import toString from 'lodash/toString'
import toInteger from 'lodash/toInteger'
import type { CurrencyCode } from '@klear/constants/currency'
import { KlearDate } from '@klear/date'
import { dateFormatted, listItemEffectiveDate } from '@klear/order-event/list-display'
import { operationalAmountLabel } from '@klear/operational/presentation'
import { useOperationals } from '@klear/ui/hooks/use-operationals'
import type { OperationalListItem } from '@klear/operational/operational-list.wire'
import type { ComboboxOption } from '@klear/ui/components/combobox'
import { Switch } from '@klear/ui/components/switch'
import { EntitySelectOrCreate } from '@/app/workflow/EntitySelectOrCreate'

export type OperationalSelectOrCreateProps = {
  supplierId: string | number
  currency: CurrencyCode
  excludeIds?: number[]
  defaultSelectedId?: number | null
  onAttach: (operationalEventId: number) => void
  onCreateNew?: () => void
}

export function OperationalSelectOrCreate({
  supplierId,
  currency,
  excludeIds,
  defaultSelectedId,
  onAttach,
  onCreateNew
}: OperationalSelectOrCreateProps) {
  const listFilters = useMemo(() => ({ supplierId: toInteger(supplierId), unclaimedByPayable: true }), [supplierId])

  const { operationals: existingOperationals, isLoading } = useOperationals(listFilters, {
    requiredFilters: ['supplierId']
  })

  const [selectedExistingId, setSelectedExistingId] = useState<number | null>(defaultSelectedId ?? null)
  const [showPast, setShowPast] = useState(false)

  const { upcoming, past } = useMemo(() => {
    const excluded = new Set(excludeIds ?? [])
    const today = KlearDate.today()
    const visible = existingOperationals.filter(
      (event: OperationalListItem) =>
        !excluded.has(event.id ?? 0) &&
        (event.amount == null || event.amount.isZero() || event.amount.currency === currency)
    )
    return {
      upcoming: visible.filter((event) => listItemEffectiveDate(event)?.isSameOrAfter(today) ?? true),
      past: visible.filter((event) => listItemEffectiveDate(event)?.isBefore(today) ?? false)
    }
  }, [currency, excludeIds, existingOperationals])

  const operationalOptions = useMemo<ComboboxOption[]>(() => {
    return (showPast ? [...upcoming, ...past] : upcoming).map((event: OperationalListItem) => {
      const idLabel = `#${event.idDisplay}`
      const titleText = event.title ?? event.number ?? event.notes ?? null
      const effectiveDate = listItemEffectiveDate(event)
      const dateText = effectiveDate ? dateFormatted(effectiveDate) : null
      const tradeText = compact([event.orderIdDisplay, event.customerName]).join(' · ') || null
      const amountText = operationalAmountLabel(event.amount)
      const left = compact([idLabel, titleText]).join(' · ')
      const label = (
        <span className="flex w-full items-center justify-between gap-4">
          <span className="truncate">{left}</span>
          <span className="ml-auto flex shrink-0 items-center gap-3 tabular-nums">
            {tradeText && <span className="truncate text-muted-foreground">{tradeText}</span>}
            {dateText && <span className="text-muted-foreground">{dateText}</span>}
            <span className="font-medium text-foreground">{amountText}</span>
          </span>
        </span>
      )
      return {
        value: toString(event.id),
        label,
        keywords: compact([idLabel, titleText, dateText, tradeText, amountText])
      }
    })
  }, [past, showPast, upcoming])

  return (
    <EntitySelectOrCreate
      inlineCreate
      isLoading={isLoading}
      selectTitle="Add an operational event"
      selectDescription="A cost this supplier billed on one of their trades. This payment can cover several, from any of their trades."
      selectFieldLabel="Operational"
      options={operationalOptions}
      selectedValue={selectedExistingId != null ? toString(selectedExistingId) : ''}
      onSelectedChange={(v) => setSelectedExistingId(v ? toInteger(v) : null)}
      onSelectConfirm={() => {
        if (selectedExistingId == null) return
        onAttach(selectedExistingId)
        setSelectedExistingId(null)
      }}
      selectConfirmLabel="Add"
      selectConfirmDisabled={selectedExistingId == null}
      selectAside={
        past.length > 0 ? (
          <label className="flex items-center gap-2 pt-1 text-sm text-muted-foreground">
            <Switch checked={showPast} onCheckedChange={setShowPast} />
            Include {past.length} earlier {past.length === 1 ? 'event' : 'events'}
          </label>
        ) : null
      }
      selectTriggerLabel="Select operational…"
      selectPlaceholder="Search operationals…"
      createTitle="Create a new operational event"
      createDescription="Build the operational event for this trade. We will walk you through the details."
      createActionLabel="Create"
      onCreateNew={onCreateNew}
    />
  )
}
